import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { X, Loader2, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { useActivityBar } from '../contexts/ActivityBarContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORANGE = '#ee7203';
const inputClass =
  'rounded-lg border-2 border-gray-300 bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-200 h-10';

const emptyForm = { first_name: '', last_name: '', title: '', phone: '', mobile: '', email: '' };

/**
 * One modal for both adding a new contact and editing an existing one (pre-filled). Saving an edit updates the contact in
 * Zoho CRM as well. Props:
 *   isOpen, onClose
 *   accountId      Zoho account the contact belongs to
 *   accountName    shown in the sub-title
 *   contact        the existing contact to edit, or null to add a new one
 *   onSaved(contact, mode)   mode is 'add' or 'edit'
 */
export default function CustomerContactModal({ isOpen, onClose, accountId, accountName = '', contact = null, onSaved }) {
  const { modalUiV2 } = useUIPreview();
  const activityBar = useActivityBar();
  const isEdit = !!contact?.id;
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      first_name: contact?.first_name || '',
      last_name: contact?.last_name || '',
      title: contact?.title || '',
      phone: contact?.phone || '',
      mobile: contact?.mobile || '',
      email: contact?.email || '',
    });
  }, [isOpen, contact?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const save = async () => {
    if (!form.last_name.trim()) return toast.error('Last name is required');
    setSaving(true);
    const body = {
      account_id: accountId,
      first_name: form.first_name.trim() || null,
      last_name: form.last_name.trim(),
      title: form.title.trim() || null,
      phone: form.phone.trim() || null,
      mobile: form.mobile.trim() || null,
      email: form.email.trim() || null,
    };
    try {
      const res = isEdit
        ? await api.put(`${API}/zoho/contact/${contact.id}`, body)
        : await api.post(`${API}/zoho/create-contact`, body);
      toast.success(isEdit ? 'Contact updated in CRM' : 'Contact added');
      onSaved?.(res.data?.contact, isEdit ? 'edit' : 'add');
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed left-0 right-0 bottom-0 flex items-center justify-center p-4 bg-black/50"
      style={{ top: modalUiV2 ? activityBar.headerHeight : 0, zIndex: 120 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="customer-contact-modal-overlay"
    >
      <div
        className="w-full max-w-[520px] rounded-3xl bg-white shadow-2xl border-2 border-orange-300 overflow-hidden"
        data-testid="customer-contact-modal"
      >
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-orange-100 bg-white/70">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}
            >
              <UserRound className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-extrabold text-gray-900 leading-tight">{isEdit ? 'Edit Contact' : 'Add New Contact'}</h3>
              <p className="text-[11px] text-gray-500 truncate">{accountName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 rounded-full p-1.5 hover:bg-gray-100 flex-shrink-0"
            data-testid="customer-contact-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">First name</Label>
              <Input value={form.first_name} onChange={set('first_name')} className={`mt-1 ${inputClass}`} data-testid="customer-contact-first" autoFocus />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Last name *</Label>
              <Input value={form.last_name} onChange={set('last_name')} className={`mt-1 ${inputClass}`} data-testid="customer-contact-last" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Job title</Label>
              <Input value={form.title} onChange={set('title')} className={`mt-1 ${inputClass}`} data-testid="customer-contact-title" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Phone</Label>
              <Input value={form.phone} onChange={set('phone')} className={`mt-1 ${inputClass}`} data-testid="customer-contact-phone" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Mobile</Label>
              <Input value={form.mobile} onChange={set('mobile')} className={`mt-1 ${inputClass}`} data-testid="customer-contact-mobile" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Email</Label>
              <Input value={form.email} onChange={set('email')} className={`mt-1 ${inputClass}`} data-testid="customer-contact-email" />
            </div>
          </div>
          {isEdit && (
            <p className="text-[11px] text-gray-500">Saving updates this contact in Zoho CRM as well.</p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} className="rounded-xl h-10 px-4 text-sm" data-testid="customer-contact-cancel">
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="rounded-xl h-10 px-5 text-sm text-white font-semibold"
              style={{ backgroundColor: ORANGE }}
              data-testid="customer-contact-save"
            >
              {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Saving…</> : (isEdit ? 'Save Changes' : 'Add Contact')}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
